import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Settings } from "lucide-react";
import { api } from "@/app/api";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700", eligible: "bg-sky-50 text-sky-700",
  provisioned: "bg-indigo-50 text-indigo-700", active: "bg-emerald-50 text-emerald-700",
  expired: "bg-slate-100 text-slate-500", void: "bg-rose-50 text-rose-700",
};
const FILTERS = ["", "pending", "eligible", "provisioned", "active", "expired", "void"];

export default function Entitlements() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [demoView, setDemoView] = useState("all");

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (demoView !== "all") params.set("demo", demoView === "test" ? "true" : "false");
    const qs = params.toString() ? `?${params}` : "";
    api.get(`/admin/entitlements${qs}`).then((r) => setData(r.data.entitlements)).catch(() => setData([]));
  }, [status, demoView]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-entitlements" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-ink">MyAE entitlements</h1>
          <p className="mt-1 text-sm text-slatey">Learner benefit entitlements — created by qualifying outcomes plus commercial qualification, never by applications alone.</p>
        </div>
        <Link to="/app/admin/entitlements/settings" data-testid="benefit-settings-link"
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-bold text-ink hover:border-cobalt hover:text-cobalt">
          <Settings className="h-4 w-4" /> Benefit settings
        </Link>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((value) => (
          <button key={value || "all"} onClick={() => setStatus(value)} data-testid={`entitlement-filter-${value || "all"}`}
            className={`min-w-max rounded-full px-4 py-2 text-xs font-bold capitalize ${status === value ? "bg-cobalt text-white" : "bg-white text-slatey border border-slate-200"}`}>{value || "All"}</button>
        ))}
        <span className="mx-1 border-l border-slate-200" />
        {[["all", "All records"], ["live", "Live only"], ["test", "TEST only"]].map(([value, label]) => (
          <button key={value} onClick={() => setDemoView(value)} data-testid={`entitlement-demo-${value}`}
            className={`min-w-max rounded-full px-4 py-2 text-xs font-bold ${demoView === value ? "bg-ink text-white" : "bg-white text-slatey border border-slate-200"}`}>{label}</button>
        ))}
      </div>

      {!data ? <p className="text-sm text-slatey">Loading…</p> : (
        <div className="overflow-x-auto rounded-3xl border border-slate-200 bg-white">
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead><tr className="border-b border-slate-100 text-xs font-bold uppercase text-slatey">
              <th className="px-5 py-3">Learner</th><th className="px-5 py-3">University / Intake</th><th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Eligible at</th><th className="px-5 py-3">Active from</th><th className="px-5 py-3">Expires</th>
            </tr></thead>
            <tbody>
              {data.map((record, index) => (
                <tr key={record.id || index} className="border-b border-slate-50 hover:bg-surface">
                  <td className="px-5 py-3">
                    <Link to={`/app/admin/entitlements/${record.id}`} data-testid={`entitlement-${record.id}`} className="font-bold text-cobalt hover:underline">
                      {record.learner_name || "Learner"}
                    </Link>
                    <p className="text-xs text-slatey">{record.application_reference} {record.is_demo && <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">TEST</span>}</p>
                  </td>
                  <td className="px-5 py-3 text-slatey">{record.provider_name}<p className="text-xs">{record.intake_label}</p></td>
                  <td className="px-5 py-3"><span className={`rounded-full px-3 py-1 text-xs font-bold capitalize ${STATUS_STYLE[record.status]}`} data-testid={`entitlement-status-${record.id}`}>{record.status}</span></td>
                  <td className="px-5 py-3 text-xs text-slatey">{record.eligible_at?.slice(0, 10) || "—"}</td>
                  <td className="px-5 py-3 text-xs text-slatey">{record.active_from?.slice(0, 10) || "—"}</td>
                  <td className="px-5 py-3 text-xs text-slatey">{record.expires_at?.slice(0, 10) || "—"}</td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-sm text-slatey">No entitlements match.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
